import type { HTMLAttributes } from 'react';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /**
   * `xs` is Figma's `body-xsm/regular` caption chip (12/16, weight 400) —
   * the onboarding "Suggested" tag. `sm`/`md` stay on `body-sm/medium`
   * (14/20, weight 500), which is what every domain card already ships.
   */
  size?: 'xs' | 'sm' | 'md';
}

const variantStyles: Record<string, string> = {
  default: 'bg-surface-secondary text-muted-foreground border border-border',
  primary: 'bg-primary-light text-primary border border-primary-light',
  success: 'bg-success-light text-success border border-success-border',
  warning: 'bg-warning-light text-warning border border-warning-border',
  danger: 'bg-danger-light text-danger border border-danger-border',
  info: 'bg-info-light text-info border border-info-border',
};

// Weight lives here rather than on the shared base class: the caption chip
// is regular, the others are medium.
const sizeStyles: Record<string, string> = {
  xs: 'px-1.5 py-0.5 text-xs leading-4 font-normal',
  sm: 'px-1.5 py-0.5 text-sm leading-5 font-medium',
  md: 'px-2 py-0.5 text-sm leading-5 font-medium',
};

export function Badge({
  variant = 'default',
  size = 'md',
  className = '',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </span>
  );
}
